import * as R from "ramda";
import { useCallback, useMemo, useRef, useState } from "react";

import { extent } from "d3-array";
import { Delaunay } from "d3-delaunay";
import { forceSimulation, forceCollide, forceY } from "d3-force";
import { line } from "d3-shape";
import { scaleLinear, scaleOrdinal, scaleTime } from "d3-scale";
import parseISO from "date-fns/fp/parseISO";
import { COLORS } from "../../common/chart.js";

import Popup from "../Popup/Popup.jsx";
import YAxis from "../Chart/YAxis.jsx";
import XAxisTrend from "../Chart/XAxisTrend.jsx";

import "./line-chart.scss";

import { NUMBER } from "../../common/format.js";

import { arrayOf, number, shape, string } from "prop-types";

const LABEL_WIDTH = 150;
const LABEL_HEIGHT = 14;

const propTypes = {
  width: number.isRequired,
  height: number,
  lines: arrayOf(
    shape({
      id: string.isRequired,
      displayName: string.isRequired,
      color: string, // optional, will fallback to using COLORS
      dataPoints: arrayOf(
        shape({
          timeFrame: string.isRequired,
          value: number.isRequired,
        })
      ).isRequired,
    })
  ),
};

const defaultProps = {
  height: 400,
  width: 600,
  lines: [],
};

const SHOW_VORONOI_OVERLAY = false;

const LineChart = (props) => {
  const svgWidth = props.width;
  const svgHeight = props.height;
  const { lines } = props;

  const svgRef = useRef();

  const [popupInfo, setPopupInfo] = useState();
  const [voronoiOverlay, setVoronoiOverlay] = useState();

  // space for axis, etc
  const margin = { top: 10, right: 10, bottom: 40, left: 60 };

  const hasLabels = lines.filter((line) => line.displayName).length > 0;

  // const shouldShowLabels = window.innerWidth > 1300 && hasLabels;
  const shouldShowLabels = hasLabels;

  const subtractLabelValue = shouldShowLabels ? LABEL_WIDTH : 10;

  const width = svgWidth - margin.left - margin.right - subtractLabelValue;
  const height = svgHeight - margin.top - margin.bottom;

  const xScale = useMemo(() => {
    // eg [Date, Date]
    const xDomain = R.compose(
      R.map(parseISO),
      extent,
      R.pluck("timeFrame"),
      R.unnest,
      R.pluck("dataPoints")
    )(lines);

    return scaleTime().range([0, width]).domain(xDomain);
  }, [lines, width]);

  const yScale = useMemo(() => {
    const yDomain = R.compose(extent, R.pluck("value"), R.unnest, R.pluck("dataPoints"))(lines);
    return scaleLinear().range([height, 0]).domain([0, yDomain[1]]).nice();
  }, [height, lines]);
  const lineGenerator = line()
    // .curve(curveBumpX)
    .x((d) => xScale(parseISO(d.timeFrame)))
    .y((d) => yScale(d.value));

  const colorScale = useMemo(
    () => scaleOrdinal().domain(R.pluck("id", lines)).range(COLORS),
    [lines]
  );

  const getColor = useCallback(
    (line) => {
      return line.color || colorScale(line.id) || "black";
    },
    [colorScale]
  );

  const findDataAtPoint = useMemo(() => {
    const points = R.chain((line) => {
      return line.dataPoints.map((point) => {
        return {
          ...point,
          line,
          x: xScale(parseISO(point.timeFrame)),
          y: yScale(point.value),
        };
      });
    })(lines);

    // d3-voronoi is deprected in favor of d3-delaunay
    const delaunay = Delaunay.from(
      points,
      (d) => d.x,
      (d) => d.y
    );

    if (SHOW_VORONOI_OVERLAY) {
      const voronoi = delaunay.voronoi([0, 0, width, height]);
      setVoronoiOverlay(voronoi);
    }

    return (x, y) => {
      const index = delaunay.find(x, y);
      const point = points[index];

      // Pythagorean theorem
      // using prop because point might be undefined
      const distance = Math.sqrt((R.prop("x", point) - x) ** 2 + (R.prop("y", point) - y) ** 2);

      // only return the point if the mouse is close enough
      return distance < 120 ? point : null;
    };
  }, [lines, height, yScale, width, xScale]);

  // TODO throttle
  const handleMouseMove = (event) => {
    // https://stackoverflow.com/a/42111623/5321793
    const rect = event.target.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const data = findDataAtPoint(x, y);

    if (data) {
      setPopupInfo({
        top: data.y,
        left: data.x,
        data,
        children: (
          <>
            <div className="line-chart__popup-title">
              {data.line.displayName} {data.timeFrame && `(${data.timeFrame})`}
            </div>
            <div className="line-chart__popup-value">{data.value.toFixed(1)}</div>
          </>
        ),
      });
    } else {
      setPopupInfo(null);
    }
  };

  const labelPositions = useMemo(() => {
    // fx used by force layout
    // targetY used below in forceY
    const positions = R.compose(
      R.reject((d) => R.isNil(d.targetY)),
      R.map((line) => {
        return {
          id: line.id,
          name: line.displayName, // just for debuging
          fx: 0, // all should have a fixed x-position
          targetY: R.compose(yScale, R.prop("value"), R.last)(line.dataPoints),
        };
      })
    )(lines);

    // forceSimulation will add "y" property
    // https://www.placeiq.com/2017/08/pointsofinterestd3forcelayout/
    // https://github.com/d3/d3-force
    forceSimulation()
      .nodes(positions)
      .force("collide", forceCollide(LABEL_HEIGHT / 2))
      .force("y", forceY((d) => d.targetY).strength(1))
      .stop()
      .tick(300);

    // sometimes a label can "jump" over another, so assign the y positions
    // based on the which ever node is in the right index for the label
    // (see link above)
    const physicalOrder = R.sortBy(R.prop("y"), positions);
    const targetOrder = R.sortBy(R.prop("targetY"), positions);
    const orderedLabelPositions = targetOrder.map((position, i) => {
      return {
        ...position,
        y: physicalOrder[i].y,
      };
    });

    return R.indexBy(R.prop("id"), orderedLabelPositions);
  }, [lines, yScale]);

  const renderLabel = useCallback(
    (labelPosition, line) => {
      if (!labelPosition) {
        return null;
      }

      const x = xScale(xScale.domain()[1]);

      return (
        line.displayName &&
        shouldShowLabels && (
          <g>
            <line
              x1={x + 10}
              y1={R.compose(yScale, R.prop("value"), R.last)(line.dataPoints)}
              x2={x + 24}
              y2={labelPosition.y}
              stroke="#101c27"
            />

            <text
              className="line-chart__line-label"
              fill="#101c27"
              fontSize={LABEL_HEIGHT}
              strokeWidth="0"
              x={x + 30}
              y={labelPosition.y}
              dy={"0.4em"}
              style={{ transition: "color 500ms" }}
            >
              {line.displayName}
            </text>
          </g>
        )
      );
    },
    [xScale, yScale]
  );

  return (
    <div className="line-chart">
      <div
        className="line-chart__popup-container"
        style={{
          position: "absolute",
          top: margin.top,
          left: margin.left,
          width: width,
          height: height,
          pointerEvents: "none",
          // backgroundColor: "rgba(0, 255, 255, 0.3)",
        }}
      >
        {/* TODO don't show the transition when the popup is unhidden */}
        <Popup
          className="line-chart__popup popup--transition-position"
          isHidden={!popupInfo}
          offset={18}
          triangleSide="bottom"
          triangleAlignment={0.1}
          flipToContain="viewport"
          nudgeToContain="viewport"
          {...popupInfo}
        />
      </div>
      <svg
        ref={svgRef}
        width={svgWidth}
        height={svgHeight}
        viewBox={`0 0 ${svgWidth} ${svgHeight}`}
      >
        <YAxis
          gridLines
          className="line-chart__y-axis--left"
          margin={margin}
          width={width}
          height={height}
          scale={yScale}
          dataType={NUMBER}
          transitionDuration={0}
        />

        {/* X-axis above y-axis for bottom line */}
        <XAxisTrend
          className="line-chart__x-axis"
          height={height}
          margin={margin}
          scale={xScale}
          tickSizeOuter={0}
          transitionDuration={0}
        />

        <g
          className="line-chart__chart-area"
          transform={`translate(${margin.left}, ${margin.top})`}
        >
          {popupInfo && (
            <line
              x1={popupInfo.left}
              y1={0}
              x2={popupInfo.left}
              y2={height}
              stroke={"#e6e6e6"}
              strokeWidth={2}
            />
          )}
          <g className="line-chart__lines">
            {lines.map((line) => {
              const labelPosition = labelPositions[line.id];

              const getPointId = (point) => `${line.id}-${point.timeFrame}`;

              // this format should match getPointId above
              const hoveredPoint =
                popupInfo && `${popupInfo.data.line.id}-${popupInfo.data.timeFrame}`;
              const lineIsHovered = R.path(["data", "line", "id"], popupInfo) === line.id;

              const lineIsDim = !R.isNil(popupInfo) && !lineIsHovered;

              return (
                <g
                  key={line.id}
                  style={{
                    opacity: lineIsDim ? 0.2 : 1,
                    transition: "opacity 500ms",
                  }}
                >
                  {renderLabel(labelPosition, line)}

                  <path
                    d={lineGenerator(line.dataPoints)}
                    style={{
                      stroke: getColor(line),
                      strokeWidth: 6,
                      strokeLinecap: "round",
                      strokeLinejoin: "round",
                    }}
                  />

                  <g className="line-chart__data-points">
                    {line.dataPoints.map((point) => {
                      const pointId = getPointId(point);

                      return (
                        <circle
                          key={pointId}
                          r={hoveredPoint === pointId ? 6 : 0}
                          cx={xScale(parseISO(point.timeFrame))}
                          cy={yScale(point.value)}
                          style={{
                            opacity: lineIsHovered ? 1 : 0,
                            fill: "white",
                            strokeWidth: 4,
                            stroke: getColor(line),
                          }}
                        />
                      );
                    })}
                  </g>
                </g>
              );
            })}
          </g>
          {voronoiOverlay && (
            <path
              className="line-chart__voronoi-overlay"
              d={voronoiOverlay.render()}
              stroke="#8e44ad"
            />
          )}
          <rect
            className="line-chart__chart-hit-area"
            x={0}
            y={0}
            width={width}
            height={height}
            style={{ fill: "#2980b9", opacity: 0 }}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setPopupInfo(null)}
          />
        </g>
      </svg>
    </div>
  );
};

LineChart.defaultProps = defaultProps;
LineChart.propTypes = propTypes;
export default LineChart;
